import React from "react";
import { Modal } from "antd";
import TextField from "../Common/TextField";
import {Editor} from '@tinymce/tinymce-react';
import {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {editorBodyModified, emailTemplateSubjectModified} from "../../store/actions/WebchatEmailTemplateAction";
import ContactSelector from "../Common/ContactSelector";
import {useActiveCompanyData} from "hooks/CompanyHooks";
import {Auth} from "aws-amplify";
import {sendTestEmail} from "../../apiService/RESTApi";
import CustomerAssetManagerModal from "../../commons/AssetManager/CustomerAssetManagerModal";
import AttributeVariablePicker from "../../commons/AttributeVariablePicker";

const WebchatEmailTemplateEditor = () => {

    const {editor} = useSelector((state) => state.WebchatEmailTemplateReducer);
    const dispatch = useDispatch();

    const [initialEditorContent, setInitialEditorContent] = useState('');
    const editorKey = useRef(0);
    const editorRef = useRef(null);

    const activeCompanyData = useActiveCompanyData();

    const debounce = (func, delay) => {
        let timeoutId;
        return function (...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    };

    const debouncedHandleEditorChange = useRef(
        debounce(() => {
            if (editorRef.current) {
                dispatch(editorBodyModified(editorRef.current.getContent()));
            }
        }, 1000)
    ).current;

    const handleEditorChange = () => {
        debouncedHandleEditorChange();
    };


    const dismissToolbarMenu = () => {
        const toolbarMenu = document.querySelector('.tox-tbtn[aria-label="Reveal or hide additional toolbar items"]');
        if (toolbarMenu) {
            toolbarMenu.click();
        }
    }

    const [isSendTestEmailModalOpen, setIsSendTestEmailModalOpen] = useState(false);
    const [isAssetManagerModalOpen, setIsAssetManagerModalOpen] = useState(false);
    const [isVariableMapModalOpen, setIsVariableMapModalOpen] = useState(false);

    const selectContactsHandleOk = async (selectedContacts) => {

        const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
        const email = currentAuthenticatedUser?.attributes?.email;

        const contactToMimic = selectedContacts[0];

        const payload = {
            customerUuid: activeCompanyData.uuid,
            customerEmailTemplateId: editor.id,
            contactId: contactToMimic,
            emailAddress: email,
        };
        const response = await sendTestEmail(payload);
        console.log("Test Email Sent", response);

    };

    const selectContactsHandleCancel = () => {
    };

    const [isEmailSubjectModalOpen, setIsEmailSubjectModalOpen] = useState(false);
    const [emailSubjectModalTextValue, setEmailSubjectModalTextValue] = useState('');
    const emailSubjectModalOnOk = () => {
        if (editor.subject !== emailSubjectModalTextValue) {
            dispatch(emailTemplateSubjectModified(emailSubjectModalTextValue));
        }
        setIsEmailSubjectModalOpen(false);
    };
    const emailSubjectModalOnCancel = () => {
        setIsEmailSubjectModalOpen(false);
    };

    // OnMounted, only run if editor ID changes, not when body changes
    useEffect(() => {
        setInitialEditorContent(editor?.body);
    }, [editor?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // OnMounted, Rerender when email subject modal is opened/closed
    useEffect(() => {
        setEmailSubjectModalTextValue(editor?.subject);
    }, [isEmailSubjectModalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="editor-container">
            <Editor
                apiKey={process.env.REACT_APP_TINY_MCE_API_KEY}
                onInit={(_evt, tinyEditor) => editorRef.current = tinyEditor}
                key={editorKey}
                initialValue={initialEditorContent}
                init={{
                    inline_styles: true,
                    branding: true,
                    content_css: [],
                    height: '100%',
                    plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen', 'insertdatetime', 'media', 'table', 'help', 'wordcount'
                    ],
                    menubar: true,
                    toolbar: 'undo redo | blocks | fontsizeinput | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help | editSubject | customViewAssets | variableMap | testButton',
                    setup: (editorApi) => {
                        editorApi.ui.registry.addButton('editSubject', {
                            text: 'Edit Email Subject',
                            onAction: () => {
                                console.log('Edit Email Subject clicked');
                                dismissToolbarMenu();
                                setIsEmailSubjectModalOpen(true);
                            }
                        });

                        editorApi.ui.registry.addButton('customViewAssets', {
                            text: 'View Assets',
                            onAction: () => {
                                console.log('View Assets clicked');
                                dismissToolbarMenu();
                                setIsAssetManagerModalOpen(true)
                            }
                        });

                        editorApi.ui.registry.addButton('variableMap', {
                            text: 'Variable Map',
                            onAction: () => {
                                console.log('View Variable Map');
                                dismissToolbarMenu();
                                setIsVariableMapModalOpen(true)
                            }
                        });

                        editorApi.ui.registry.addButton('testButton', {
                            text: 'Send Test Email',
                            onAction: () => {
                                dismissToolbarMenu();
                                setIsSendTestEmailModalOpen(true)

                            }
                        });
                    },
                }}
                onEditorChange={handleEditorChange}
            />
            <ContactSelector
                isOpen={isSendTestEmailModalOpen}
                toggleModal={() => setIsSendTestEmailModalOpen(!isSendTestEmailModalOpen)}
                allowMultipleSelection={false}
                footerText={`When you press Send Email you will receive a formatted and personalized a sample email for the customer you select. Please check your spam folder too.`}
                buttonText="Send Email"
                onOkCallback={selectContactsHandleOk}
                onCancelCallback={selectContactsHandleCancel}
            />

            <CustomerAssetManagerModal
                open={isAssetManagerModalOpen}
                onCancel={() => {
                    setIsAssetManagerModalOpen(!isAssetManagerModalOpen);
                }}
            />

            {activeCompanyData?.databaseId && (
                <AttributeVariablePicker
                    open={isVariableMapModalOpen}
                    onCancel={() => {
                        setIsVariableMapModalOpen(!isVariableMapModalOpen);
                    }}
                    customerId={activeCompanyData.databaseId}
                />
            )}

            <Modal
                open={isEmailSubjectModalOpen}
                className="modal"
                overlayClassName="overlay"
                title="Edit Email Subject"
                okText="Apply"
                onOk={emailSubjectModalOnOk}
                cancelText="Cancel"
                onCancel={emailSubjectModalOnCancel}
                closeIcon={true}
                onRequestClose={emailSubjectModalOnCancel}
                shouldCloseOnOverlayClick={true}
            >
                <TextField
                    id="email-subject-field"
                    label="Email Subject"
                    initialValue={emailSubjectModalTextValue}
                    onChange={(value) => {
                        setEmailSubjectModalTextValue(value);
                    }}
                />
            </Modal>
        </div>
    );
};

export default WebchatEmailTemplateEditor;